import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone, ArrowLeft } from 'lucide-react';
import './LearnMorePage.css';
import Popup from './Popup';
import QRCodeComponent from './QRCode';

const LearnMorePage = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPhoneNumber, setPopupPhoneNumber] = useState('');
  const [popupFormattedPhoneNumber, setPopupFormattedPhoneNumber] = useState('');
  const navigate = useNavigate();
  const { service } = useParams();

  const serviceData = {
    'iva-service-requests': {
      title: 'IVA for Service Requests',
      subtitle: 'Faster Repairs. Lower Costs. Higher Uptime.',
      description: 'Agencies need to keep vehicles on the road - not waiting on paperwork. IVA automates maintenance communication so issues get reported, scheduled and resolved faster.',
      content: 'With IVA, staff or drivers can report issues naturally - "Schedule a repair for Bus 412" - and IVA validates details, creates service requests and updates records automatically. It also proactively asks about related issues ("Any other maintenance concerns with this vehicle?"), preventing follow-up calls and ensuring complete repair in one step.',
      features: [
        {
          title: 'Maximize Fleet Uptime',
          description: 'Report and schedule repairs instantly through natural conversation, reducing downtime and keeping vehicles on the road.'
        },
        {
          title: 'Streamline Operations',
          description: 'Automates repetitive reporting and data entry, cutting administrative effort and freeing staff for higher-value work.'
        },
        {
          title: 'Reduce Costs & Errors',
          description: 'Captures accurate details the first time and integrates with FASTER for quicker resolutions and fewer mistakes.'
        }
      ],
      image: '/IVAService.jpg'
    },
    'ai-technician-app': {
      title: 'Empowering Technicians for Every Challenge',
      subtitle: 'From Diagnosis Through Resolution',
      description: 'Technicians spend hours troubleshooting issues, accessing manuals and coordinating with support risking delays, inconsistent solutions and frustrated maintenance teams.',
      content: 'Our AI Technician App Platform and Intelligent Support System digitize and streamline the entire process, giving technicians real-time guidance and our team handles complex issues, end-to-end.',
      features: [
        {
          title: 'Central, Streamlined Operations',
          description: 'Manage the entire maintenance process - from diagnosis to resolution - in one secure system. Our AI handles guidance, troubleshooting and expert support to eliminate delays and data loss.'
        },
        {
          title: 'Automated Intelligence & Accountability',
          description: 'AI-powered assistance and expert review keep technicians on schedule, fully auditable, and solution-focused - without adding staff or manual tracking.'
        },
        {
          title: 'Transparent, Connected Technician Experience',
          description: 'Technicians receive real-time guidance and have accessible support, backed by AI and staff who ensure every maintenance experience is accurate, clear and efficient.'
        }
      ],
      image: '/Technician.jpg'
    },
    'ecolane-iva-trip-management': {
      title: 'IVA for Trip Management',
      subtitle: 'Smarter, Simpler Rider Communication',
      description: 'With conversational AI designed to mirror how human staff speak - friendly, efficient, and natural - riders can manage their trips anytime, anywhere, while agencies automate repetitive calls and deliver faster, more accessible service.',
      content: '',
      features: [
        {
          title: '24/7 Accessible Service',
          description: 'Riders can check, change, or cancel trips anytime with conversational AI - improving accessibility and satisfaction.'
        },
        {
          title: 'Automated Efficiency',
          description: 'Reduces up to 70% of routine calls, freeing staff and lowering operational costs.'
        },
        {
          title: 'Scalable & Future-Ready',
          description: 'Adapts to demand and evolves toward predictive, personalized rider communication.'
        }
      ],
      image: '/IVATrip.jpg'
    },
    'eligibility': {
      title: 'Simplifying Eligibility for Every Rider',
      subtitle: 'From Application Through Approval',
      description: 'Agencies spend hours chasing paperwork, reconciling spreadsheets and managing manual reviews risking delays, compliance issues and frustrated applicants.',
      content: 'Our Eligibility Platform and Managed Eligibility Service digitize and streamline the entire process, giving agencies flexibility to run it themselves or have our team handle it, end-to-end.',
      features: [
        {
          title: 'Central, Streamlined Operations',
          description: 'Manage the entire eligibility process - from application to determination - in one secure system. Our team handles intake, review and approval to eliminate duplication and data loss.'
        },
        {
          title: 'Automated Compliance & Accountability',
          description: 'Automation and expert review keep agencies on schedule, fully auditable, and ADA-compliant - without adding staff or manual tracking.'
        },
        {
          title: 'Transparent, Connected Rider Experience',
          description: 'Applicants will soon receive real-time updates. They will also have accessible communication, supported by staff who ensure every rider\'s experience is accurate, clear and compliant.'
        }
      ],
      image: '/Eligibility.jpg'
    }
  };

  const currentService = serviceData[service] || serviceData['iva-service-requests'];

  const handleBackClick = () => {
    navigate('/home');
  };

  return (
    <>
      <div className="learn-more-background" style={{ backgroundImage: `url(/Gradient.png)` }}>
      </div>
      
      <div className={`learn-more-content ${service === 'ecolane-iva-trip-management' ? 'ecolane-trip-management' : ''} ${service === 'iva-service-requests' ? 'iva-service-requests' : ''}`}>
        <div className="learn-more-header">
          <button className="back-button" onClick={handleBackClick}>
            <ArrowLeft size={48} />
          </button>
        </div>
        <div className="learn-more-main">
          <div className="two-column-layout">
            <div className="image-column">
              <div className="service-image-container">
                <img src={currentService.image} alt={`${currentService.title} Service`} className="service-image" />
              </div>
            </div>
            <div className="text-column">
              <h1 className="service-title">{currentService.title}</h1>
              <p className="service-subtitle">{currentService.subtitle}</p>
              <p className="service-description">{currentService.description}</p>
              <p className="service-content">{currentService.content}</p>
              
              <div className="features-section">
                {currentService.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="button-container">
                <button
                  className="try-it-out-button"
                  onClick={() => {
                    if (service === 'iva-service-requests') {
                      setPopupPhoneNumber('+14782455134');
                      setPopupFormattedPhoneNumber('+1 (478) 245-5134');
                      setShowPopup(true);
                    }
                    if (service === 'ecolane-iva-trip-management') {
                      setPopupPhoneNumber('+12525422916');
                      setPopupFormattedPhoneNumber('+1 (252) 542-2916');
                      setShowPopup(true);
                    }
                    if (service === 'eligibility') {
                      window.location.href = 'https://qa-testing.ecolane.com/eligibility';
                    }
                    if (service === 'ai-technician-app') {
                      window.location.href = 'https://faster-mob.transit-technologies.ai';
                    }
                  }}
                >
                  {service === 'eligibility' || service === 'ai-technician-app' ? (
                    'See More'
                  ) : (
                    <>
                      <Phone size={13} />
                      Try it out
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Popup show={showPopup} onClose={() => setShowPopup(false)}>
        <QRCodeComponent phoneNumber={popupPhoneNumber} formattedPhoneNumber={popupFormattedPhoneNumber} />
      </Popup>
    </>
  );
};

export default LearnMorePage;
